import {
  CardActionArea,
  CardContent,
  Collapse,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import Icon from '@components/Icon/Icon';
import IconButton from '@components/IconButton';

import { formatPhone } from '@utils/format';

import { RoleChip, StyledContactCard } from './ContactCard.style';

const messages = defineMessages({
  contact: {
    id: 'contactCard.contact',
    defaultMessage: 'Contact',
  },
  address: {
    id: 'contactCard.address',
    defaultMessage: 'Address',
  },
  confirmDelete: {
    id: 'contactForm.confirmDelete',
    defaultMessage: 'Are you sure you want to delete {name} as a contact?',
  },
});

export interface ContactCardData {
  id: string;
  name: string;
  chipLabel: string;
  chipVariant?: string;
  subtitle?: string;
  phone?: string | null;
  email?: string | null;
  location?: string | null;
}

interface ContactCardProps {
  data: ContactCardData;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  canUpdate: boolean;
  canDelete: boolean;
  loading: boolean;
}

export default function ContactCard({
  data,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  canUpdate,
  canDelete,
  loading,
}: ContactCardProps) {
  const { formatMessage } = useIntl();
  const { name, chipLabel, chipVariant, phone, email, location } = data;

  return (
    <StyledContactCard expanded={expanded} elevation={0}>
      <CardActionArea onClick={onToggle} disableRipple>
        <CardContent>
          <Tooltip title={chipLabel} disableInteractive>
            <RoleChip
              label={chipLabel}
              size="small"
              contactType={chipVariant}
            />
          </Tooltip>
          <Typography variant="body1" sx={{ fontWeight: 600, mt: 1.5 }}>
            {name}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
            {phone || email || location ? (
              <>
                {phone && (
                  <Icon
                    name="phone"
                    size="xs"
                    type="fas"
                    color="text.disabled"
                  />
                )}
                {email && (
                  <Icon
                    name="envelope"
                    size="xs"
                    type="fas"
                    color="text.disabled"
                  />
                )}
                {location && (
                  <Icon
                    name="location-dot"
                    size="xs"
                    type="fas"
                    color="text.disabled"
                  />
                )}
              </>
            ) : (
              <span style={{ height: 10 }} />
            )}
          </Stack>
        </CardContent>
      </CardActionArea>

      <Collapse in={expanded}>
        <Divider />
        <CardContent>
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              {phone || email ? (
                <Typography
                  variant="overline"
                  color="text.disabled"
                  sx={{ fontSize: '0.625rem' }}
                >
                  {formatMessage(messages.contact)}
                </Typography>
              ) : (
                <span />
              )}
              <Stack direction="row" spacing={0.5}>
                {canUpdate && (
                  <IconButton
                    disabled={loading}
                    color="grey.500"
                    name="pencil"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit();
                    }}
                    iconType="fas"
                    size={12}
                  />
                )}
                {canDelete && (
                  <ConfirmationDialog
                    onConfirm={async () => onDelete()}
                    title={formatMessage(messages.confirmDelete, {
                      name,
                    })}
                  >
                    {({ handleOpen }) => (
                      <IconButton
                        disabled={loading}
                        color="grey.500"
                        name="trash"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpen();
                        }}
                        iconType="fas"
                        size={12}
                      />
                    )}
                  </ConfirmationDialog>
                )}
              </Stack>
            </Stack>

            {(phone || email) && (
              <Stack spacing={0.5}>
                {phone && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icon
                      name="phone"
                      size="xs"
                      type="fas"
                      color="primary.main"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatPhone(phone)}
                    </Typography>
                  </Stack>
                )}
                {email && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icon
                      name="envelope"
                      size="xs"
                      type="fas"
                      color="primary.main"
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ wordBreak: 'break-all' }}
                    >
                      {email}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}

            {location && (
              <Stack spacing={0.5}>
                <Typography
                  variant="overline"
                  color="text.disabled"
                  sx={{ fontSize: '0.625rem' }}
                >
                  {formatMessage(messages.address)}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Icon
                    name="location-dot"
                    size="xs"
                    type="fas"
                    color="primary.main"
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {location}
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Collapse>
    </StyledContactCard>
  );
}
